import React, { useEffect, Suspense } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/partials/header";
import Sidebar from "@/components/partials/sidebar";
import MobileMenu from "@/components/partials/sidebar/MobileMenu";
import Settings from "@/components/partials/settings";
import useWidth from "@/hooks/useWidth";
import Footer from "@/components/partials/footer";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import { ToastContainer } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import Loading from "@/components/Loading";
import { motion } from "framer-motion";
import { handleMobileMenu } from "@/store/layout";
import { ListingPageRoot } from "@/components/listing/ListingLayout";

const Layout = () => {
  const { width, breakpoints } = useWidth();
  const navigate = useNavigate();
  const { isAuth, user } = useSelector((state) => state.auth);
  const mobileMenuOpen = useSelector((state) => state.layout.mobileMenu);
  const sidebarCollapsed = useSelector((state) =>
    Boolean(state.layout?.isCollapsed),
  );
  const dispatch = useDispatch();

  // Require an authenticated ADMIN, not just a present token. This is
  // defense-in-depth: real enforcement is server-side, but this stops a
  // non-admin token from rendering the admin UI or firing admin API calls.
  const isAdmin = Boolean(user && user.isAdmin);

  useEffect(() => {
    if (!isAuth || !user || !isAdmin) {
      navigate("/");
    }
  }, [isAuth, navigate, user, isAdmin]);
  const location = useLocation();

  // Do not mount any admin content for non-admins.
  if (!isAuth || !user || !isAdmin) {
    return null;
  }

  return (
    <>
      <ToastContainer />
      <Header />
      <Sidebar />

      {mobileMenuOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-[9998] bg-slate-900/45 backdrop-blur-[2px] md:hidden"
          aria-label="Close navigation menu"
          onClick={() => dispatch(handleMobileMenu(false))}
        />
      ) : null}

      <div
        className={`fixed inset-y-0 left-0 z-[9999] w-[min(300px,92vw)] max-w-full transition-transform duration-300 ease-out md:hidden ${
          mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full pointer-events-none"
        }`}
      >
        <MobileMenu />
      </div>

      <Settings />

      <div
        className={`font-jakarta ml-0 min-h-screen pt-16 transition-[margin] duration-200 ease-out ${
          sidebarCollapsed ? "md:ml-16" : "md:ml-52"
        }`}
      >
        <Suspense fallback={<Loading />}>
          <motion.div
            key={location.pathname}
            initial="pageInitial"
            animate="pageAnimate"
            exit="pageExit"
            variants={{
              pageInitial: {
                opacity: 0,
                y: 12,
              },
              pageAnimate: {
                opacity: 1,
                y: 0,
              },
              pageExit: {
                opacity: 0,
                y: -12,
              },
            }}
            transition={{
              type: "spring",
              damping: 28,
              stiffness: 260,
            }}
            className="min-h-screen bg-gradient-to-br from-stone-100/90 via-stone-50 to-teal-50/25 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"
          >
            <div className="p-4 sm:p-6">
              <div className="mb-5">
                <Breadcrumbs />
              </div>
              <ListingPageRoot>
                <Outlet />
              </ListingPageRoot>
            </div>
          </motion.div>
        </Suspense>
      </div>

      {width > breakpoints.md && <Footer />}
    </>
  );
};

export default Layout;
